import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { getConsentStatus, getSessionId } from '../components/CookieConsent';
import { trackPageView } from '../services/analyticsService';
import { UserAuth } from './AuthContext';

const AnalyticsContext = createContext();

export const AnalyticsContextProvider = ({ children }) => {
    const [consentStatus, setConsentStatus] = useState('pending');
    const [sessionId, setSessionId] = useState(null);
    const { user } = UserAuth();

    // Initialize consent status on mount
    useEffect(() => {
        const status = getConsentStatus();
        setConsentStatus(status);
        if (status === 'accepted') {
            setSessionId(getSessionId());
        }
    }, []);

    // Handle consent changes from CookieConsent component
    const handleConsentChange = useCallback((newStatus) => {
        setConsentStatus(newStatus);
        if (newStatus === 'accepted') {
            setSessionId(getSessionId());
        } else {
            setSessionId(null);
        }
    }, []);

    // Track a page view (only if consent given)
    const trackPage = useCallback(async (path) => {
        if (consentStatus !== 'accepted' || !sessionId) {
            return;
        }

        try {
            await trackPageView({
                path,
                sessionId,
                userId: user?.uid || null,
                referrer: document.referrer || null
            });
        } catch (error) {
            // Silently fail - analytics shouldn't break the app
            console.debug('Analytics tracking failed:', error.message);
        }
    }, [consentStatus, sessionId, user?.uid]);

    return (
        <AnalyticsContext.Provider
            value={{
                consentStatus,
                sessionId,
                trackPage,
                handleConsentChange
            }}
        >
            {children}
        </AnalyticsContext.Provider>
    );
};

export const useAnalytics = () => {
    return useContext(AnalyticsContext);
};
